import dayjs from "dayjs";
import { merge } from "lodash";
import { renderToString } from "react-dom/server";

import { serverResponseFormat } from "@similarweb/pro-logic-components";

import { CHART_COLORS } from "app/constants/ChartColors";
import { AdIntelligenceAdvertiserPerformanceOvertimeGraphWidgetFilters } from "app/components/widget/widget-filters/AdIntelligenceAdvertiserPerformanceOvertimeGraphWidgetFilters";
import { GraphWidget } from "app/components/widget/widget-types/GraphWidget";
import { translate } from "app/filters/i18nFilter";
import { EDataType } from "app/pages/digital-marketing/ad-intelligence/advertisers/overview/components/types";
import {
    getChartConfig,
    YAxisTitle,
} from "app/pages/digital-marketing/ad-intelligence/common/overview/configs/PerformanceOverTimeGraphConfig";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { SwTrack } from "app/services/SwTrack";

const fetchService = DefaultFetchService.getInstance();

const getEmptyGraphProps = (spendTotal: number, impressionsTotal: number) => [
    {
        name: translate("ad.intelligence.overview.performance.over.time.widget.spend"),
        id: EDataType.SPEND,
        total: spendTotal,
        color: CHART_COLORS.mobileWeb[0],
        visible: true,
        yAxis: 0,
        data: [],
    },
    {
        name: translate("ad.intelligence.overview.performance.over.time.widget.impressions"),
        id: EDataType.IMPRESSIONS,
        total: impressionsTotal,
        color: CHART_COLORS.mobileWeb[1],
        visible: true,
        yAxis: 1,
        data: [],
    },
];

function createDataFetcherFactory(widget) {
    return {
        fetch: async () => {
            const { keys, from, to, country } = widget.apiParams;
            const { isWindow } = widget.durationObject.forAPI;

            const apiParams = {
                key: keys,
                from,
                to,
                country,
                isWindow,
                ...(isWindow ? { latest: BasicDurations.LAST_TWENTY_EIGHT_DAYS } : {}),
            };
            return fetchService.get(`${widget._metricConfig.endpoint}/GraphV2/unified`, apiParams);
        },
    };
}

export class AdIntelligencePerformanceOvertimeGraphWidget extends GraphWidget {
    private dataForSite;

    private totals;

    private selectedBreakdownData;

    initWidget(widgetConfig, context) {
        super.initWidget(widgetConfig, context);
    }

    static getWidgetDashboardType() {
        return "Graph";
    }

    static getWidgetResourceType() {
        return "Graph";
    }

    static getWidgetMetadataType() {
        return "AdIntelligencePerformanceOvertimeGraph";
    }

    private onLegendClick(that, isHidden) {
        const seriesItem = that.chartConfig.series.find((item) => item.id === this.id);
        if (seriesItem) {
            seriesItem.visible = seriesItem.visible === false;
            SwTrack.all.trackEvent("Click Filter", isHidden ? "remove" : "click", seriesItem.name);
        }
        let index = 0;
        let text = "ad.intelligence.overview.performance.over.time.widget.spend";
        if (seriesItem.id == EDataType.IMPRESSIONS) {
            index = 1;
            text = "ad.intelligence.overview.performance.over.time.widget.impressions";
        }
        that.chartConfig.yAxis[index].title.text = isHidden
            ? undefined
            : renderToString(<YAxisTitle>{translate(text)}</YAxisTitle>);
    }

    public isPptSupported = () => false;

    private dataFetcherFactory = () => {
        return {
            create: createDataFetcherFactory,
        };
    };

    public getFetcherFactory() {
        return this.dataFetcherFactory();
    }

    protected validateData(data) {
        return this.totals?.impressions && this.totals?.spend;
    }

    private transformData() {
        const breakdown = this.dataForSite?.[this.apiParams.timeGranularity.toLowerCase()];
        this.selectedBreakdownData = Object.entries(breakdown || {});

        return this.selectedBreakdownData.reduce(
            (acc, [date, item]) => {
                const dateValue = dayjs.utc(date, serverResponseFormat).valueOf();
                (acc[0].data as number[][]).push([dateValue, item.spend.value]);
                (acc[1].data as number[][]).push([dateValue, item.impressions.value]);
                return acc;
            },
            getEmptyGraphProps(this.totals?.spend, this.totals?.impressions),
        );
    }

    public async onNewDataReceived(response): Promise<void> {
        // GraphV2/unified nests per-domain data under `data` and the granularity buckets under `total`.
        this.dataForSite = response?.data?.[this.apiParams.keys]?.total;
        this.totals = this.dataForSite?.total;

        await super.onNewDataReceived({ Data: response });
    }

    public getProUrl() {
        const { webSource, duration, country, key } = this._getProUrlParams();
        const urlParams = {
            country,
            webSource,
            key,
            duration,
        };

        return this._swNavigator.href(this._metricConfig.state, urlParams);
    }

    public getChartSeries(data) {
        return this.transformData();
    }

    public getChartOptions(formattedData) {
        const chartOptions = this.metadata.chartOptions;
        chartOptions.type = "line";
        chartOptions.margin = [48, 58, 60, 65];
        chartOptions.viewOptions = this._viewOptions;
        chartOptions.height = 220;
        return chartOptions;
    }

    static getFiltersComponent() {
        return AdIntelligenceAdvertiserPerformanceOvertimeGraphWidgetFilters;
    }

    public async callbackOnGetData(response: any): Promise<void> {
        const formattedData = this.getChartSeries(response.Data);

        this.setMetadata(formattedData);
        const chartOptions = this.getChartOptions(formattedData);

        this.chartConfig = this.getHighChartsConfig(chartOptions, formattedData);

        this.legendItems = formattedData.map((item) => {
            return {
                name: item.name,
                color: item.color,
                onClick: this.onLegendClick.bind(item, this),
            };
        });
    }

    getHighChartsConfig(chartOptions, formattedData) {
        const chartConfig = getChartConfig(
            this._params.timeGranularity,
            DurationService.getDurationData(this._params.duration).raw.to,
            [EDataType.SPEND, EDataType.IMPRESSIONS],
            !this.totals?.impressions && !this.totals?.spend,
            this.selectedBreakdownData,
            false,
            EDataType.VISITS,
        );
        const { xAxis, yAxis, ...rest } = chartConfig;

        const isSingleMonth = this.selectedBreakdownData.length === 1;

        const extendedChartOptions = {
            options: merge({}, rest, { chart: chartOptions }),
            series: formattedData,
            xAxis,
            yAxis,
            plotOptions: {
                series: {
                    marker: {
                        enabled: isSingleMonth,
                        states: {
                            hover: {
                                enabled: isSingleMonth,
                            },
                        },
                    },
                },
            },
        };

        return extendedChartOptions;
    }
}

AdIntelligencePerformanceOvertimeGraphWidget.register();
